import React, { useEffect, useRef } from "react";
import { TextFieldProps, TextField } from "@mui/material";
import useDebounce from "helpers/useDebounce";

export interface DebouncedTextProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  delayMs?: number;
  label?: string;
  textFieldProps?: TextFieldProps;
  formatter?: (value: string) => string;
}

const DebouncedText = (props: DebouncedTextProps) => {
  const { value, onChange, disabled, textFieldProps, formatter } = props;
  const delay = props.delayMs || 500;
  const inputRef = useRef<HTMLInputElement>(null);

  const [internalValue, setInternalValue] = React.useState(value);

  const debouncedValue = useDebounce(internalValue, delay);

  const handleInputChange = React.useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      // Apply formatter if provided
      setInternalValue(formatter ? formatter(evt.target.value) : evt.target.value);
    },
    [setInternalValue]
  );

  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (debouncedValue !== value) {
      onChange(debouncedValue);
    }
  }, [debouncedValue]);

  useEffect(() => {
    const handleWheel = (_: WheelEvent) => {
      if (document.activeElement === inputRef.current) {
        inputRef.current?.blur();
      }
    };

    if (textFieldProps?.type === "number") {
      document.addEventListener("wheel", handleWheel);

      return () => {
        document.removeEventListener("wheel", handleWheel);
      };
    }
  }, [textFieldProps?.type, inputRef.current]);

  return (
    <TextField
      inputRef={inputRef}
      label={props.label}
      disabled={disabled}
      {...textFieldProps}
      value={internalValue}
      onChange={handleInputChange}
    />
  );
};

export default DebouncedText;
